<script>
	import { onMount } from "svelte";
	import { getApi } from "../../service/api";
	import { logAppAll as all, logApiTableAll as allTables, menu, menuSub, menuSubSub } from "../../stores";
	import { Dates } from "../../utils/date";
	import { parseLogFile } from "../../utils/logParser";
	import TableToolbar from "../../components/TableToolbar.svelte";
	import AppLogChart from "../../components/AppLogChart.svelte";
	import Pagination from "../../components/Pagination.svelte";
	import Tab from "../../components/Tab.svelte";
	import "@andypf/json-viewer"

	// 상수 정의
	const LAMBDA_BASE_URL = "https://serverless.sleepwave.ai/applog";
	const FC_BASE_URL = "https://serverless.blaupunkt-solum.cn/applog";
	const S3_BASE_URL = "https://formebuds-app-log.s3.ap-northeast-2.amazonaws.com";	
	const OSS_BASE_URL = "https://blaupunkt-app-log.oss-cn-hangzhou.aliyuncs.com";
	const PAGE_SIZES = [50, 100, 150, 200];

	// 현재 선택된 서버 확인
	let selectedServer = "";
	let isChinaServer = false;

	// 상태 관리
	let logData = [];
	let allOldData = []; // 구버전 전체 데이터 저장
	let isLoading = false;
	let hasSearched = false; // 검색 실행 여부
	let currentLoadingTab = ""; // 현재 로딩 중인 탭

	// 페이징 관련
	let currentPage = 1;
	let totalPages = 1;
	let totalItems = 0;
	let pageSize = 50;
	let start = 0
	let end = 50

	// 상세 팝업
	let selectedItem = null
	let showDetailModal = false

	//차트 확장 팝업
	let selectedChart = null
	let selectedKey = null
	let showExpandModal = false

	// 검색 조건
	let keywordUser = ""
	
	// 버전 관리 (기본: 신버전)
	let activeTab = "new";
	
	// 정렬 관리
	let sortField = "";
	let sortOrder = ""; // "asc" | "desc"

	// 환경 변수 결정 함수
	const getEnvironment = () => {
		switch (selectedServer) {
			case "localhost" || "dev-api-backstage.sleepwave.ai":
				return "dev";
			case "stg-api-backstage.sleepwave.ai":
				return "stg";
			case "kr-api-backstage.sleepwave.ai":
				return "kr";
			case "us-api-backstage.sleepwave.ai":
				return "us";
			case "api-backstage.blaupunkt-solum.cn":
				isChinaServer = true;
				return "prd";
			case "dev-api-backstage.blaupunkt-solum.cn":
				isChinaServer = true;
				return "dev";
			default:
				return "dev";
		}
	};

	// 탭 정의
	const tabs = [
		{ key: "new", text: "Latest" },
		{ key: "old", text: "Old" }
	];

	// 검색 실행
	const handleSearch = async () => {
		if (activeTab === "new") {
			// 신버전: 이메일 필수, API 호출
			if (!keywordUser.trim()) {
				alert("회원 이메일을 입력해주세요.");
				return;
			}
			currentPage = 1;
			await fnFetchLogs();
		} else {
			// 구버전: 프론트 필터링만 수행, API 호출 없음
			fnFilterOldVersionData();
		}
	};

	// 검색어 초기화
	const handleReset = () => {
		keywordUser = "";
		logData = [];
		hasSearched = false;
		currentPage = 1;
		totalItems = 0;
		totalPages = 1;
		sortField = "";
		sortOrder = "";
	};

	// 탭 변경 핸들러
	const handleTabClick = async (tab) => {
		console.log("탭 변경:", activeTab, "->", tab);
		
		// 로딩 중이면 즉시 중단
		if (isLoading) {
			console.log("로딩 중 탭 변경 - 기존 작업 무효화");
			isLoading = false;
			currentLoadingTab = "";
		}
		
		activeTab = tab;
		
		if (tab === "old") {
			// 신버전 -> 구버전: 즉시 전체 목록 조회
			console.log("구버전으로 전환, 전체 목록 조회 시작");
			currentPage = 1;
			await fnFetchLogs();
		} else {
			// 구버전 -> 신버전: 목록 초기화
			console.log("신버전으로 전환, 목록 초기화");
			logData = [];
			allOldData = [];
			hasSearched = false;
			currentPage = 1;
			totalItems = 0;
			totalPages = 1;
		}
	};

	// 목록 새로고침
	const handleReload = async () => {
		// 신버전은 이메일 필수, 구버전은 선택사항
		if (activeTab === "new" && !keywordUser.trim()) {
			alert("회원 이메일을 입력해주세요.");
			return;
		}
		await fnFetchLogs();
	};

	// 정렬 핸들러
	const handleSort = (field) => {
		if (sortField === field) {
			// 같은 필드 클릭: 정렬 순서 토글
			sortOrder = sortOrder === "ASC" ? "DESC" : "ASC";
		} else {
			// 다른 필드 클릭: 새로운 필드로 내림차순 정렬
			sortField = field;
			sortOrder = "DESC";
		}
		
		// 정렬 적용
		applySorting();
	};

	// 정렬 상태 확인 (LogApi.svelte와 동일)
	const getSortClass = (field) => {
		if (sortField !== field) return "sortable";
		return sortOrder === "ASC" ? "sortable sort-asc" : "sortable sort-desc";
	};

	// 정렬 적용
	const applySorting = () => {
		if (!sortField || !sortOrder) return;
		
		logData = [...logData].sort((a, b) => {
			let aValue, bValue;
			
			switch (sortField) {
				case "email":
					aValue = getEmailFromKey(a.key).toLowerCase();
					bValue = getEmailFromKey(b.key).toLowerCase();
					break;
				case "size":
					aValue = a.size || 0;
					bValue = b.size || 0;
					break;
				case "careDate":
					aValue = makeCareDate(a.key);
					bValue = makeCareDate(b.key);
					break;
				case "updatedAt":
					aValue = a.updatedAt || "";
					bValue = b.updatedAt || "";
					break;
				default:
					return 0;
			}
			
			if (sortOrder === "ASC") {
				return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
			} else {
				return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
			}
		});
		
		// 페이징 재계산
		fnProcessPaging();
	};


	// 모달 토글 함수
	const toggleDetailModal = (item) => {
		showDetailModal = !showDetailModal;
		if (!showDetailModal) {
			// 모달 닫을 때 입력값 초기화
			selectedItem = null
		} else {
			selectedItem = item
		}
	};
	
	const toggleExpandModal = (key, data) => {
		showExpandModal = !showExpandModal;
		if (!showExpandModal) {
			// 모달 닫을 때 입력값 초기화
			selectedKey = null
			selectedChart = null
		} else {
			selectedKey = key;
			selectedChart = data;
		}
	};

	// 람다 API로 로그 목록 조회 (신버전/구버전)
	const fnFetchLogs = async () => {
		isLoading = true;
		currentLoadingTab = activeTab; // 현재 로딩 중인 탭 기록
		
		try {
			if (activeTab === "old") {
				// 구버전: 전체 목록 조회 후 프론트에서 필터링
				await fnFetchOldVersionLogs();
			} else {
				// 신버전: 서버에서 필터링된 결과 조회
				await fnFetchNewVersionLogs();
			}
		} catch (error) {
			console.error("로그 조회 오류:", error);
			// 탭이 변경되지 않았을 때만 에러 처리
			if (currentLoadingTab === activeTab) {
				alert("로그 조회 중 오류가 발생했습니다.");
				logData = [];
				hasSearched = true;
			}
		}
		
		// 탭이 변경되지 않았을 때만 로딩 완료 처리
		if (currentLoadingTab === activeTab) {
			isLoading = false;
		}
		currentLoadingTab = "";
	};

	// 신버전 로그 조회
	const fnFetchNewVersionLogs = async () => {
		const environment = getEnvironment();
		const encodedEmail = encodeURIComponent(keywordUser.trim());
		const url = `${isChinaServer ? FC_BASE_URL : LAMBDA_BASE_URL}/${environment}?email=${encodedEmail}`;
		
		const response = await fetch(url, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
			}
		});
		
		const result = await response.json();
		
		// 탭이 변경되지 않았을 때만 결과 처리
		if (currentLoadingTab === activeTab) {
			if (result.resultCode === 1000) {
				logData = result.data || [];
				hasSearched = true;
				// 정렬이 설정되어 있다면 적용
				if (sortField && sortOrder) {
					applySorting();
				} else {
					fnProcessPaging();
				}
			} else {
				alert(result.resultDescKo || "로그 조회에 실패했습니다.");
				logData = [];
				hasSearched = true;
			}
		}
	};

	// 구버전 로그 조회 (전체 목록 + 프론트 필터링)
	const fnFetchOldVersionLogs = async () => {
		const url = `${isChinaServer ? FC_BASE_URL : LAMBDA_BASE_URL}/old`;
		
		const response = await fetch(url, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
			}
		});
		
		const result = await response.json();
		
		// 탭이 변경되지 않았을 때만 결과 처리
		if (currentLoadingTab === activeTab) {
			if (result.resultCode === 1000) {
				const allData = result.data || [];
				allOldData = allData; // 전체 데이터 저장
				
				// 프론트에서 필터링 수행
				fnFilterOldVersionData();
			} else {
				alert(result.resultDescKo || "로그 조회에 실패했습니다.");
				logData = [];
				allOldData = [];
				hasSearched = true;
			}
		}
	};

	// 구버전 데이터 필터링 (API 호출 없이 프론트에서만)
	const fnFilterOldVersionData = () => {
		if (allOldData.length === 0) {
			logData = [];
			hasSearched = true;
			fnProcessPaging();
			return;
		}

		// 프론트에서 필터링 (이메일이 있을 경우에만)
		if (keywordUser.trim()) {
			logData = allOldData.filter(item => {
				// 기존 구조에서 이메일 추출하여 필터링
				const email = getEmailFromKey(item.key);
				return email.toLowerCase().includes(keywordUser.trim().toLowerCase());
			});
		} else {
			// 이메일이 없으면 전체 목록
			logData = allOldData;
		}
		
		hasSearched = true;
		currentPage = 1; // 검색 시 첫 페이지로
		
		// 정렬이 설정되어 있다면 적용
		if (sortField && sortOrder) {
			applySorting();
		} else {
			fnProcessPaging();
		}
	};

	// 페이징 처리
	const fnProcessPaging = () => {
		totalItems = logData.length || 0;
		totalPages = Math.ceil(totalItems / pageSize) || 1;

		start = (currentPage - 1) * pageSize;
		end = start + pageSize;

		if (start >= totalItems) {
			// 현재 페이지가 범위를 벗어나면 첫 페이지로
			currentPage = 1;
			start = 0;
			end = pageSize;
		}

		if (end > totalItems) {
			end = totalItems;
		}
	};

	const fnProcessDetail = async (fileKey) => {
		try {
			// S3에서 직접 파일 읽기
			const encodedFileKey = encodeURIComponent(fileKey);
			const url = `${isChinaServer ? OSS_BASE_URL : S3_BASE_URL}/${encodedFileKey}`;
			
			console.log("URL:", url);
			
			const response = await fetch(url, {
				method: "GET",
			});
			
			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}
			
			// 텍스트로 로그 파일 내용 읽기
			const logContent = await response.text();
			
			if (logContent) {
				toggleDetailModal(logContent);
			} else {
				alert("로그 파일이 비어있습니다.");
			}
		} catch (error) {
			console.error("로그 상세 조회 오류:", error);
			alert("로그 상세 조회 중 오류가 발생했습니다.");
		}
	}

	const makeCareDate = (key) => {
		// 새로운 구조: stg/trace/sleepwave.spark@gmail.com/D/20250923/181251.log
		try {
			const pathParts = key.split("/");
			if (pathParts.length >= 5) {
				const datePart = pathParts[4]; // 20250923
				const fileName = pathParts[5]; // 181251.log
				const timePart = fileName.split(".")[0]; // 181251
				
				const year = datePart.substring(0, 4);
				const month = datePart.substring(4, 6);
				const day = datePart.substring(6, 8);
				const hour = timePart.substring(0, 2);
				const minute = timePart.substring(2, 4);
				const second = timePart.substring(4, 6);
				
				return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
			}
		} catch (error) {
			console.warn("날짜 파싱 오류:", error, key);
		}
		
		// 기존 구조 fallback (_로 구분)
		const dateArr = key.split("_");
		if (dateArr.length >= 4) {
			const date = dateArr[2];
			const time = dateArr[3];

			const year = date.substring(0, 4);
			const month = date.substring(4, 6);
			const day = date.substring(6, 8);
			const hour = time.substring(0, 2);
			const minute = time.substring(2, 4);
			const second = time.substring(4, 6);
			return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
		}
		
		return "날짜 불명";
	}

	const makeByteStr = (bytes, decimals = 2)=> {
		if (!bytes && bytes !== 0) return '0 B';
		const k = 1024;
		const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		const size = parseFloat((bytes / Math.pow(k, i)).toFixed(decimals));
		return `${size} ${units[i]}`;
	}

	const handlePageSizeChange = (event) => {
		pageSize = parseInt(event.target.value);
		currentPage = 1;
		fnProcessPaging();
	};

	const handlePageChange = (page) => {
		if (page < 1 || page > totalPages) return;
		currentPage = page;
		fnProcessPaging();
	};

	// 다운로드 함수
	const handleDownload = async (fileKey) => {
		try {
			// fileKey는 _ 형태이므로 / 로 복원하여 S3/OSS에서 파일 조회
			const actualKey = fileKey.replace(/_/g, "/");
			const encodedFileKey = encodeURIComponent(actualKey);
			const url = `${isChinaServer ? OSS_BASE_URL : S3_BASE_URL}/${encodedFileKey}`;

			const response = await fetch(url, {
				method: "GET",
			});

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const blob = await response.blob();

			// 파일명은 _ 형태의 원본 fileKey 사용
			const fileName = fileKey;

			// 다운로드 링크 생성 및 클릭
			const downloadUrl = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = downloadUrl;
			link.download = fileName;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(downloadUrl);
		} catch (error) {
			console.error("파일 다운로드 오류:", error);
			alert("파일 다운로드 중 오류가 발생했습니다.");
		}
	};

	// 키보드 이벤트 처리
	const handleKeyPress = (event) => {
		if (event.key === "Enter") {
			handleSearch();
		}
	};

	// 중국 서버 여부 확인
	$: {
		selectedServer = localStorage.getItem("selectedServer") || "";
		isChinaServer = selectedServer === "dev-api-backstage.blaupunkt-solum.cn" || selectedServer === "api-backstage.blaupunkt-solum.cn";
	}

	const getEmailFromKey = (key) => {
		// 새로운 구조에서 이메일 추출: stg/trace/sleepwave.spark@gmail.com/D/20250923/181251.log
		try {
			const pathParts = key.split("/");
			if (pathParts.length >= 3) {
				return pathParts[2]; // sleepwave.spark@gmail.com
			}
		} catch (error) {
			console.warn("이메일 파싱 오류:", error, key);
		}
		
		// 기존 구조 fallback (_로 구분)
		const parts = key.split("_");
		if (parts.length >= 2) {
			return parts[1];
		}
		
		return "이메일 불명";
	};

	onMount(() => {
		selectedServer = localStorage.getItem("selectedServer") || "";
		menu.set("로그 관리");
		menuSub.set("App 로그");
		menuSubSub.set("");
		// 초기 로딩 제거 - 사용자가 이메일 입력 후 검색해야 함
	});
</script>

<div class="card mb-4">
	<div class="table-responsive text-nowrap">
		<!-- 탭 메뉴 -->
		<Tab {tabs} {activeTab} onTabChange={handleTabClick} />
		
		<!-- 검색 영역 -->
		<div class="card-header">
			<div class="row align-items-center">
				<div class="col-md-12">
					<div class="d-flex align-items-center gap-2">
						<input
							type="email"
							class="form-control form-control-sm"
							placeholder={activeTab === "old" ? "회원 이메일 (선택사항)" : "회원 이메일을 입력하세요 (필수)"}
							bind:value={keywordUser}
							on:keypress={handleKeyPress}
							style="width: 300px;"
							required={activeTab === "new"}
						/>
						<button class="btn btn-primary btn-sm" on:click={handleSearch} disabled={isLoading || (activeTab === "new" && !keywordUser.trim())}>
							{#if isLoading}
								<div class="spinner-border spinner-border-sm me-1" role="status"></div>
							{:else}
								<i class="bx bx-search"></i>
							{/if}
							검색
						</button>
						<button class="btn btn-outline-secondary btn-sm" on:click={handleReset} disabled={isLoading}>
							<i class="bx bx-refresh"></i>
							초기화
						</button>
					</div>
				</div>
			</div>
		</div>
		
		<div class="ms-auto" style="padding: 5px;">
			<TableToolbar
				{totalItems}
				{currentPage}
				{totalPages}
				{pageSize}
				pageSizes={PAGE_SIZES}
				onPageSizeChange={handlePageSizeChange}
				onReload={handleReload}
			/>
		</div>
		<div class="fade-container">
			<div class="table-container">
				<table class="custom-table">
					<thead>
						<tr style="text-align:center">
							<th width="80">No</th>
							<th width="40" class={getSortClass('email')} on:click={() => handleSort('email')}>Email</th>
							<th width="40" class={getSortClass('size')} on:click={() => handleSort('size')}>파일크기</th>
							<th width="40" class={getSortClass('careDate')} on:click={() => handleSort('careDate')}>케어 일시</th>
							<th width="40" class={getSortClass('updatedAt')} on:click={() => handleSort('updatedAt')}>업로드 일시</th>
							<th width="40">다운로드</th>
						</tr>
					</thead>
					<tbody>
						{#if isLoading}
							<tr>
								<td colspan="6" class="text-center py-4">
									<div class="spinner-border spinner-border-sm me-2" role="status"></div>
									로딩 중...
								</td>
							</tr>
						{:else if !hasSearched}
							<tr>
								<td colspan="6" class="text-center py-4" style="font-size: 12px;">
									회원 이메일을 입력하고 검색 버튼을 클릭하세요.
								</td>
							</tr>
						{:else if logData.length === 0}
							<tr>
								<td colspan="6" class="text-center py-4" style="font-size: 12px;">
									해당 이메일의 로그 데이터가 없습니다.
								</td>
							</tr>
						{:else}
							{#each logData.slice(start, end) as item, index}
								<tr class={index % 2 == 0 ? "even-model" : "odd-model"} style="text-align:center; font-size: 12px;">
									<td>{totalItems - ((currentPage - 1) * pageSize + index)}</td>
									<td on:click={()=>fnProcessDetail(item.key)} style="cursor: pointer;">{getEmailFromKey(item.key)}</td>
									<td on:click={()=>fnProcessDetail(item.key)} style="cursor: pointer;">{makeByteStr(item.size)}</td>
									<td on:click={()=>fnProcessDetail(item.key)} style="cursor: pointer;">{makeCareDate(item.key)}</td>
									<td on:click={()=>fnProcessDetail(item.key)} style="cursor: pointer;">{item.updatedAt}</td>
									<td>
										<button
											class="btn btn-sm btn-outline-primary"
											on:click={() => handleDownload(item.key)}
											title="파일 다운로드"
										>
											<i class="bx bx-download"></i>
										</button>
									</td>
								</tr>
							{/each}
						{/if}
					</tbody>
				</table>
			</div>		
		</div>
		<Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
	</div>
	<style>
		.table-container {
			padding: 0 15px;
			width: 100%;
		}
		.custom-table {
			border-collapse: collapse;
			border: 1px solid #ddd;
			margin-bottom: 5px;
			font-family: "Gulim";
			font-size: 11px;
			box-shadow: 0 2px 3px rgba(0, 0, 0, 0.1);
			width: 100%;
		}

		/* 헤더 스타일 */
		.custom-table thead tr {
			text-align: center;
			background-color: #f2f2f2;
			border-bottom: 2px solid #ddd;
		}

		/* 셀 공통 스타일 */
		.custom-table th,
		.custom-table td {
			padding: 4px;
			border: 1px solid #ddd;
			text-align: center;
			font-size: 11px;
		}

		/* 정렬 가능한 헤더 스타일 (LogApi.svelte와 동일) */
		.sortable {
			cursor: pointer;
			user-select: none;
			transition: background-color 0.2s;
			position: relative;
		}

		.sortable:hover {
			background-color: #e6e6e6 !important;
		}

		/* 정렬 아이콘 */
		.sort-asc::after {
			content: ' ▲';
			font-size: 10px;
			color: #666;
		}

		.sort-desc::after {
			content: ' ▼';
			font-size: 10px;
			color: #666;
		}

		/* 짝수/홀수 행 스타일 */
		.even-model {
			background-color: #ffffff;
			padding: 4px;
			border: 1px solid #ddd;
			font-size: 11px;
		}

		.odd-model {
			background-color: #f9f9f9;
			padding: 4px;
			border: 1px solid #ddd;
			font-size: 11px;
		}

		/* 호버 효과 */
		.custom-table tbody tr:hover {
			background-color: #f0f8ff !important;
			cursor: pointer;
		}

	</style>
</div>

<!-- 모달 영역 -->
{#if showDetailModal}
	<div class="modal-overlay">
		<div
			class="modal fade {showDetailModal ? 'show' : ''}"
			style="display: block;"
			tabindex="-1"
			aria-modal="true"
			role="dialog"
		>
			<div class="modal-dialog modal-dialog-centered modal-xl">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">앱 로그 상세</h5>
						<button type="button" class="btn-close" on:click={()=>toggleDetailModal("")} />
					</div>
					<div class="modal-body">
						<div class="row row-cols-1 row-cols-md-2 g-3">
							{#each Object.entries(parseLogFile(selectedItem)) as [key, data]}
								<div class="col">
									<div class="chart-container position-relative" style="height: 300px;">
										<i class="bx bx-expand position-absolute" 
											style="right: 10px; cursor: pointer;"
											on:click={()=>toggleExpandModal(key,data)}/>
										<AppLogChart chartKey={key} chartData={data} />
									</div>
								</div>
							{/each}
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="modal-backdrop fade show" />
	</div>
{/if}

<!-- 차트 확장 영역 -->
{#if showExpandModal}
	<div class="modal-overlay">
		<div
			class="modal fade {showExpandModal ? 'show' : ''}"
			style="display: block;"
			tabindex="-1"
			aria-modal="true"
			role="dialog"
		>
			<div class="modal-dialog modal-dialog-centered modal-xl">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">{selectedKey}</h5>
						<button type="button" class="btn-close" on:click={()=>toggleExpandModal("","")} />
					</div>
					<div class="modal-body">
						<div class="chart-container" style="height: 600px;">
							<AppLogChart 
								chartKey={selectedKey} 
								chartData={selectedChart} 
								labelFontSize={10} 
								legendFontSize={20}
							/>
						</div>
					</div>                          
				</div>
			</div>
		</div>
		<div class="modal-backdrop fade show" />
	</div>
{/if}


